import { EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import { createReducer, on, Action } from '@ngrx/store';

import { SensorActuatorBarnMapping } from '../../models/farm-result.model';
import { Barn, SettingModuleState } from '../../models/setting.model';
import * as settingActions from '../actions/setting.actions';

function selectIdNumber(b: Barn): number {
  return b.id;
}

function sortByName(a: Barn, b: Barn): number {
  return a.name.localeCompare(b.name);
}

export const barnAdapter: EntityAdapter<Barn> = createEntityAdapter<Barn>({
  selectId: selectIdNumber,
  sortComparer: sortByName,
});

export const sensorActuatorBarnAdapter: EntityAdapter<SensorActuatorBarnMapping> =
  createEntityAdapter<SensorActuatorBarnMapping>({});

export const initialState = <SettingModuleState>{
  barns: barnAdapter.getInitialState([]),
  selectedBarn: <Barn>{},
  isLoaded: false,
  dayOffset: undefined,
  observationSwitch: undefined,
  rvsSettings: undefined,
  sensorActuatorBarns: sensorActuatorBarnAdapter.getInitialState([]),
  unitOfMeasureSettings: undefined,
  veterinarianSettings: undefined,
  finalSetupSettings: undefined,
  discountSettings: undefined,
};

const settingReducer = createReducer(
  initialState,
  on(settingActions.loadBarnsSuccess, (state, { barns }) => ({
    ...state,
    selectedBarn: <Barn>{},
    barns: barnAdapter.setAll(barns, state.barns),
    isLoaded: true,
  })),
  on(settingActions.upsertBarnSuccess, (state, { barn }) => ({
    ...state,
    selectedBarn: barn,
    barns: barnAdapter.upsertOne(barn, state.barns),
  })),
  on(settingActions.deleteBarnSuccess, (state, { barn }) => ({
    ...state,
    selectedBarn: <Barn>{},
    barns: barnAdapter.removeOne(barn.id, state.barns),
  })),
  on(settingActions.upsertBarn, (state, { barn }) => ({
    ...state,
    selectedBarn: barn,
  })),
  on(settingActions.setSelectedBarn, (state, { barnId }) => ({
    ...state,
    selectedBarn:
      !!barnId && !!state.barns.entities[barnId] ? state.barns.entities[barnId] : <Barn>{},
  })),
  on(settingActions.setObservationSwitch, (state, { observationSwitch }) => ({
    ...state,
    observationSwitch,
  })),
  on(settingActions.setDayOffset, (state, { dayOffset }) => ({
    ...state,
    dayOffset,
  })),
  on(settingActions.setRVSSettings, (state, { value }) => ({
    ...state,
    rvsSettings: value,
    isLoaded: true,
  })),
  on(settingActions.setUnitOfMeasureSettings, (state, { value }) => ({
    ...state,
    unitOfMeasureSettings: value,
    isLoaded: true,
  })),
  on(settingActions.loadFarmResultSettingsSuccess, (state, { sensorActuatorBarns }) => ({
    ...state,
    sensorActuatorBarns: sensorActuatorBarnAdapter.setAll(
      sensorActuatorBarns,
      state.sensorActuatorBarns,
    ),
    isLoaded: true,
  })),
  on(settingActions.upsertFarmResultSettingsSuccess, (state, { sensorActuatorBarns }) => ({
    ...state,
    sensorActuatorBarns: sensorActuatorBarnAdapter.upsertMany(
      sensorActuatorBarns,
      state.sensorActuatorBarns,
    ),
  })),
  on(settingActions.upsertVeterinarianSettings, (state, { veterinarianSettings }) => ({
    ...state,
    veterinarianSettings,
    isLoaded: true,
  })),
  on(settingActions.setFinalSetupSettings, (state, { value }) => ({
    ...state,
    finalSetupSettings: value,
    isLoaded: true,
  })),
  on(settingActions.setDiscountSettigs, (state, { discountSettings }) => ({
    ...state,
    discountSettings,
    isLoaded: true,
  })),
);

export function reducer(state: SettingModuleState | undefined, action: Action) {
  return settingReducer(state, action);
}
